import React, { useState, useContext } from 'react'
import PropTypes from 'prop-types'

import defaultProps from '../../../utils/propTypes/defaultProps'
import createRandomID from '../../../utils/createRandomID'
import { handleLocalisationWithZip } from '../../../utils/requests/handleLocalisation'
import { APIContext } from '../../../context/APIContext'
import { getEnvironment } from '../../../utils/appUtils'
import { openUrl } from '../../../utils/UrlUtils'

import MacroContainer from '../../_layout/Macro/MacroContainer'
import SearchAutocomplete from '../../molecules/SearchAutocomplete/SearchAutocomplete'
import LocalisationHeadline from '../../molecules/LocalisationHeadline/LocalisationHeadline'

const randomId = createRandomID('m009')
const SEARCH_FIELD_ID = `${randomId}-search-field-id`
const POPOVER_ID = `${randomId}-popover-id`

const M009 = ({ serviceType, headline, info, url, keyword, clientMapping, regionMapping }) => {
  const [completeHeadline] = useState(() => {
    // placeholder replacement only relevant for navigator variant; should be deleted with ticket https://bugs.onedev.aok.de/browse/PCMREFRESH-6479
    if (headline?.text?.includes('%KEYWORD%') && headline?.placeholder?.text) {
      return {
        ...headline,
        text: headline.text.replace(/%KEYWORD%/g, headline.placeholder.text),
      }
    }

    return headline
  })

  const [searchFieldTyping, setSearchFieldTyping] = useState(false)
  const [searchString, setSearchString] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [currentAutocompleteResults, setCurrentAutocompleteResults] = useState([])

  const apiContext = useContext(APIContext)
  const [apiUrl] = useState(apiContext[getEnvironment('ZD_LBS_LOCALISATION_ZIP')] || '')

  const handleChangeInput = (e) => {
    setSearchFieldTyping(true)
    setErrorMessage('')
    setSearchString(e.target.value)
  }

  const handleRequestAutocompleteData = (query, successCb) => {
    // reset state
    setCurrentAutocompleteResults([])

    handleLocalisationWithZip(apiUrl, {
      zip: query,
      returnAllLocations: true,
      success: (locations) => {
        const autocompleteList = locations.map((location) => location.address)

        setCurrentAutocompleteResults(locations)
        successCb(autocompleteList)
      },
      error: (_error) => {},
    })
  }

  const handleAutocompleteSelect = (selectedAddress) => {
    const selectedLocationResult = currentAutocompleteResults.find((location) => location.address === selectedAddress)

    if (!selectedLocationResult) return

    setSelectedLocation(selectedLocationResult)
    setSearchFieldTyping(false)
    setSearchString(selectedAddress)
  }

  const handleSubmit = () => {
    if (!selectedLocation) return

    let targetUrl

    switch (serviceType) {
      case 'affiliate':
        targetUrl = getAffiliateUrl()
        break

      case 'navigator':
        targetUrl = getNavigatorUrl()
        break

      case 'event':
        targetUrl = getEventUrl()
        break

      default:
        return
    }

    if (targetUrl) {
      openUrl(targetUrl, '_blank')
    }
  }

  const getAffiliateUrl = () => {
    if (!url) return

    return url.replace(/%ZIP%/g, encodeURIComponent(searchString))
  }

  const getNavigatorUrl = () => {
    if (!clientMapping) return

    const clientId = selectedLocation?.client?.id
    let searchUrl = clientMapping[clientId]

    searchUrl = searchUrl.replace(/%ZIP%/g, encodeURIComponent(searchString))
    searchUrl = searchUrl.replace(/%QUERY%/g, keyword)

    return searchUrl
  }
  const getEventUrl = () => {
    if (!clientMapping) return

    const clientId = selectedLocation?.client?.id
    let searchUrl = clientMapping[clientId]

    const regionId = selectedLocation.region ? selectedLocation.region.id : null
    if (regionMapping && regionId && regionId in regionMapping) {
      searchUrl = regionMapping[regionId]
    }

    searchUrl = searchUrl.replace(/%ZIP%/g, encodeURIComponent(searchString))

    return searchUrl
  }

  return (
    <MacroContainer dataTrackingName="M009" className="m009">
      <LocalisationHeadline headline={completeHeadline} allowRelocalisation={false} />

      {info && <p className="mt-16 text-16 leading-24">{info}</p>}

      <div className="grid 600:gap-16 600:grid-cols-6 840:grid-cols-8 720:gap-24 1000:gap-32">
        <SearchAutocomplete
          autocompleteProps={{
            variant: 'light',
          }}
          searchFieldProps={{
            id: SEARCH_FIELD_ID,
            label: 'PLZ/Ort eingeben',
            labelVisibility: 'hide',
            buttonIsDisabled: !!searchFieldTyping,
            inputProps: {
              type: 'text',
              name: 'q',
              value: searchString,
              placeholder: 'PLZ/Ort eingeben',
            },
            ariaDescribedby: POPOVER_ID,
            error: errorMessage,
          }}
          className="mt-24 840:mt-32 600:col-span-4"
          onInputChange={handleChangeInput}
          onRequestAutocompleteData={handleRequestAutocompleteData}
          onSelect={handleAutocompleteSelect}
          onSubmit={handleSubmit}
        />
      </div>
    </MacroContainer>
  )
}

M009.propTypes = {
  serviceType: PropTypes.oneOf(['navigator', 'affiliate', 'event']),
  headline: PropTypes.shape({
    text: PropTypes.string,
    tag: PropTypes.string,
    placeholder: PropTypes.shape({
      text: PropTypes.string,
    }),
  }),
  info: PropTypes.string,
  url: PropTypes.string,
  keyword: PropTypes.string,
  clientMapping: PropTypes.shape({
    1: PropTypes.string,
    2: PropTypes.string,
    3: PropTypes.string,
    4: PropTypes.string,
    5: PropTypes.string,
    6: PropTypes.string,
    7: PropTypes.string,
    8: PropTypes.string,
    9: PropTypes.string,
    10: PropTypes.string,
    11: PropTypes.string,
  }),
  regionMapping: PropTypes.object,
}

M009.defaultProps = {
  headline: {
    text: defaultProps.HEADLINE,
  },
}

export default M009
